import React, { useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

const COMPANY_TYPE_LABELS = {
  landscaping: 'Landscaping Company',
  construction: 'Construction Company',
  tree_company: 'Tree Company',
  debris_hauling: 'Debris Hauling Company',
  equipment_rental: 'Equipment Rental Company',
};

const SERVICE_NAMES = {
  tree_removal: 'Tree Removal',
  tree_trimming: 'Tree Trimming & Pruning',
  stump_grinding: 'Stump Grinding',
  emergency_services: 'Emergency Tree Services',
  lot_clearing: 'Lot Clearing',
  brush_clearing: 'Brush Clearing',
  debris_hauling: 'Debris Hauling',
  wood_chipping: 'Wood Chipping',
  right_of_way: 'Right-of-Way Clearing',
  vegetation_management: 'Vegetation Management',
};


const SIZE_LABELS = {
  small: 'Small (Under $1,000)',
  medium: 'Medium ($1,000-$10,000)',
  large: 'Large ($10,000-$50,000)',
  enterprise: 'Enterprise (Over $50,000)',
};

function CompanyReviewStep({ data, termsAccepted, onTermsChange }) {
  const [showTermsModal, setShowTermsModal] = useState(false);

  const selectedServices = Object.entries(data.services_provided || {}).filter(
    ([, value]) => value.needed
  );

  return (
    <div className="review-step">
      <h5 className="mb-4">Review Your Information</h5>
      <p className="text-muted mb-4">
        Please review your information before submitting. Use the Previous button to make changes.
      </p>

      <div className="review-section">
        <h6>Company Information</h6>
        <div className="review-item">
          <span className="review-label">Company Name:</span>
          <span className="review-value">{data.company_name}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Company Type:</span>
          <span className="review-value">{COMPANY_TYPE_LABELS[data.company_type] || data.company_type}</span>
        </div>
        {data.website && (
          <div className="review-item">
            <span className="review-label">Website:</span>
            <span className="review-value">{data.website}</span>
          </div>
        )}
        {data.year_established && (
          <div className="review-item">
            <span className="review-label">Year Established:</span>
            <span className="review-value">{data.year_established}</span>
          </div>
        )}
        <div className="review-item">
          <span className="review-label">Address:</span>
          <span className="review-value">
            {data.address}, {data.city}, {data.state} {data.zip_code}
          </span>
        </div>
      </div>

      <div className="review-section">
        <h6>Primary Contact</h6>
        <div className="review-item">
          <span className="review-label">Name:</span>
          <span className="review-value">
            {data.contact_first_name} {data.contact_last_name}
            {data.contact_title && ` (${data.contact_title})`}
          </span>
        </div>
        <div className="review-item">
          <span className="review-label">Email:</span>
          <span className="review-value">{data.contact_email}</span>
        </div>
        <div className="review-item">
          <span className="review-label">Phone:</span>
          <span className="review-value">{data.contact_phone}</span>
        </div>
      </div>

      <div className="review-section">
        <h6>Service Areas ({data.service_areas.length} states)</h6>
        <div className="d-flex flex-wrap gap-1">
          {data.service_areas.sort().map((code) => (
            <Badge key={code} bg="success" className="fw-normal">
              {STATE_NAMES[code] || code}
            </Badge>
          ))}
        </div>
      </div>

      <div className="review-section">
        <h6>Services Provided</h6>
        {selectedServices.length === 0 ? (
          <p className="text-muted">No services selected</p>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            {selectedServices.map(([id]) => (
              <Badge key={id} bg="success" className="fw-normal">
                {SERVICE_NAMES[id] || id}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="review-section">
        <h6>Project Information</h6>
        {data.typical_project_size && (
          <div className="review-item">
            <span className="review-label">Typical Project Size:</span>
            <span className="review-value">{SIZE_LABELS[data.typical_project_size] || data.typical_project_size}</span>
          </div>
        )}
        {data.how_heard_about_us && (
          <div className="review-item">
            <span className="review-label">How Heard About Us:</span>
            <span className="review-value">{data.how_heard_about_us}</span>
          </div>
        )}
        {data.additional_notes && (
          <div className="review-item">
            <span className="review-label">Additional Notes:</span>
            <span className="review-value">{data.additional_notes}</span>
          </div>
        )}
      </div>

      <div className="terms-section mt-4 pt-4 border-top">
        <Form.Check
          type="checkbox"
          id="company-terms-checkbox"
          checked={termsAccepted}
          onChange={(e) => onTermsChange(e.target.checked)}
          label={
            <span>
              I have read and agree to the{' '}
              <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTermsModal(true);
                }}
              >
                Terms of Use and Privacy Policy
              </button>
            </span>
          }
        />
      </div>

      <Modal show={showTermsModal} onHide={() => setShowTermsModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Terms of Use and Privacy Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Terms of Use</h5>
          <p>
            By submitting this registration form, you agree to the following terms and conditions:
          </p>
          <ol>
            <li className="mb-2">
              <strong>Accuracy of Information:</strong> You certify that all information provided in this registration is accurate and complete to the best of your knowledge.
            </li>
            <li className="mb-2">
              <strong>Authorization:</strong> You confirm that you are authorized to represent your company and enter into agreements on its behalf.
            </li>
            <li className="mb-2">
              <strong>Service Engagement:</strong> You understand that registration does not guarantee service availability and that all work is subject to subcontractor availability and scheduling.
            </li>
            <li className="mb-2">
              <strong>Communication:</strong> You consent to receive communications from United Arborist regarding service quotes, scheduling, updates, and other relevant information.
            </li>
            <li className="mb-2">
              <strong>Payment Terms:</strong> You agree to adhere to the payment terms established for any services rendered through United Arborist.
            </li>
          </ol>

          <h5 className="mt-4">Privacy Policy</h5>
          <p>
            United Arborist is committed to protecting your privacy. Here's how we handle your data:
          </p>
          <ol>
            <li className="mb-2">
              <strong>Data Collection:</strong> We collect the information you provide in this form, including company details, contact information, service areas, and service requirements.
            </li>
            <li className="mb-2">
              <strong>Data Use:</strong> Your information is used to match you with qualified subcontractors and to communicate with you about our services.
            </li>
            <li className="mb-2">
              <strong>Data Sharing:</strong> We may share relevant portions of your information with subcontractors to facilitate service delivery.
            </li>
            <li className="mb-2">
              <strong>Data Security:</strong> We implement appropriate security measures to protect your company's information from unauthorized access or disclosure.
            </li>
            <li className="mb-2">
              <strong>Data Retention:</strong> We retain your information for as long as your registration is active or as required by law.
            </li>
            <li className="mb-2">
              <strong>Your Rights:</strong> You may request to view, update, or delete your information at any time by contacting us.
            </li>
          </ol>

          <p className="mt-4 text-muted small">
            Last updated: January 2026
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTermsModal(false)}>
            Close
          </Button>
          <Button
            variant="success"
            onClick={() => {
              onTermsChange(true);
              setShowTermsModal(false);
            }}
          >
            I Accept
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CompanyReviewStep;
